import React, { useEffect, useState } from 'react';
import { Box } from '@material-ui/core';
import { useSnackbar } from 'notistack';
import {
  emailIsValidRe,
  getUserRoleList,
  sentenceCase,
  wait,
} from 'globals/utils';
import defaultLocales from 'globals/locales/default';
import { UserRole } from 'globals/types';
import FsSimpleInput from 'ui-elements/form-elements/FsDefaultInput';
import FsDefaultSelect from 'ui-elements/form-elements/FsDefaultSelect';
import FsBodyBold from 'ui-elements/typography/FsBodyBold';
import FsPendingButton from 'ui-elements/buttons/FsPendingButton';
import FsFormGridWrapper from 'ui-elements/form-elements/FsFormGridWrapper';
import FsFormGridItem from 'ui-elements/form-elements/FsFormGridItem';
import FsFormFullWidthContainer from 'ui-elements/form-elements/FsFormFullWidthContainer';
import FsPhoneNumberInput from 'ui-elements/form-elements/FsPhoneNumberInput';
import FsCheckboxWithLabel from 'ui-elements/form-elements/FsCheckboxWithLabel';
import { Caption } from './UserEditForm.style-elements';

export interface UserEditFormProps {
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  role?: string;
  onClose?: () => void;
}

const UserEditForm: React.FC<UserEditFormProps> = ({ onClose, ...props }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [firstName, setFirstName] = useState<string>(props.firstName || '');
  const [lastName, setLastName] = useState<string>(props.lastName || '');
  const [email, setEmail] = useState<string>(props.email || '');
  const [phone, setPhone] = useState<string>(props.phone || '');
  const [role, setRole] = useState<string>(props.role || '');
  const [isOtherUsers, setIsOtherUsers] = useState<boolean>(false);
  const [pending, setPending] = React.useState<boolean>(false);
  const [isValid, setIsValid] = React.useState<boolean>(false);

  const fields = [
    {
      required: true,
      name: defaultLocales.firstName.toLowerCase(),
      value: firstName,
      label: defaultLocales.firstName,
      onChange: (e: React.ChangeEvent<HTMLInputElement>): void => {
        setFirstName(e.target?.value);
      },
    },
    {
      name: defaultLocales.lastName.toLowerCase(),
      value: lastName,
      label: defaultLocales.lastName,
      onChange: (e: React.ChangeEvent<HTMLInputElement>): void => {
        setLastName(e.target?.value);
      },
    },
    {
      required: true,
      name: defaultLocales.email.toLowerCase(),
      value: email,
      disabled: true,
      label: defaultLocales.email,
      onChange: (e: React.ChangeEvent<HTMLInputElement>): void => {
        setEmail(e.target?.value);
      },
    },
    {
      required: true,
      name: 'phone',
      value: phone,
      label: defaultLocales.phoneNumber,
      country: 'gb',
      onChange: (phoneNumber: string): void => {
        setPhone(phoneNumber);
      },
    },
    {
      required: true,
      isSelect: true,
      options: getUserRoleList(),
      name: 'role',
      value: role,
      label: defaultLocales.role,
      onChange: (e: React.ChangeEvent<HTMLInputElement>): void => {
        setRole(e.target?.value);
      },
    },
    {
      name: 'other-users',
      checked: isOtherUsers,
      label: defaultLocales.canSeeOtherUsers,
      onChange: (e: React.ChangeEvent<HTMLInputElement>): void => {
        setIsOtherUsers(e.target?.checked);
      },
    },
  ];

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setPending(true);
    // eslint-disable-next-line no-console
    console.log('Save changes', {
      firstName,
      lastName,
      email,
      phone,
      role,
    });
    await wait(1000);
    setPending(false);
    if (onClose) onClose();
    enqueueSnackbar(defaultLocales.saveChanges, {
      variant: 'success',
    });
  };

  const [firstNameField, lastNameField, ...otherFields] = fields;

  useEffect(() => {
    const isFirstNameValid = firstName.length >= 3;
    const isRoleValid =
      !!role && (Object as any).values(UserRole).includes(role);
    const isEmailValid = !!email.match(emailIsValidRe);
    const isPhoneValid = phone.length >= 5;
    setIsValid(isFirstNameValid && isRoleValid && isEmailValid && isPhoneValid);
  }, [firstName, lastName, email, phone, role]);

  return (
    <Box marginTop={2}>
      <form onSubmit={handleSubmit}>
        <Caption>
          {defaultLocales.name} |{' '}
          <FsBodyBold component="span">
            {firstName} {lastName}
          </FsBodyBold>
        </Caption>
        <FsFormGridWrapper>
          {[firstNameField, lastNameField].map(
            ({ name, onChange, ...otherProps }) => (
              <FsFormGridItem key={name}>
                <FsSimpleInput
                  {...{ ...otherProps, name }}
                  onChange={
                    onChange as (e: React.ChangeEvent<HTMLInputElement>) => void
                  }
                  fullWidth
                  fsVariant="outlined"
                />
              </FsFormGridItem>
            )
          )}
        </FsFormGridWrapper>
        {otherFields.map(
          ({ name, onChange, isSelect, options, ...otherProps }) => (
            <FsFormFullWidthContainer key={name}>
              {name === 'role' && isSelect && options && (
                <FsDefaultSelect
                  {...{ ...otherProps, name, options }}
                  onChange={
                    onChange as (e: React.ChangeEvent<HTMLInputElement>) => void
                  }
                  fullWidth
                  fsVariant="outlined"
                />
              )}
              {name === 'phone' && (
                <FsPhoneNumberInput
                  {...otherProps}
                  onChange={onChange as (phoneNumber: string) => void}
                  placeholder={`${sentenceCase(name)} *`}
                />
              )}
              {name === 'other-users' && (
                <FsCheckboxWithLabel
                  {...{
                    ...otherProps,
                    name,
                    options,
                    checked: !!otherProps?.checked,
                  }}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>): void => {
                    setIsOtherUsers(e.target?.checked);
                  }}
                />
              )}
              {name !== 'role' &&
                name !== 'phone' &&
                name !== 'other-users' && (
                  <FsSimpleInput
                    {...{ ...otherProps, name }}
                    onChange={
                      onChange as (
                        e: React.ChangeEvent<HTMLInputElement>
                      ) => void
                    }
                    fullWidth
                    fsVariant="outlined"
                  />
                )}
            </FsFormFullWidthContainer>
          )
        )}
        <Box display="flex" justifyContent="flex-end" flexGrow={1}>
          <FsPendingButton
            disabled={!isValid}
            type="submit"
            variant="contained"
            color="primary"
            pending={pending}
            onClick={() => undefined}
          >
            {defaultLocales.saveChanges}
          </FsPendingButton>
        </Box>
      </form>
    </Box>
  );
};

export default UserEditForm;
